import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Container } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdminUsers, deleteAdminUser, changeAdminPassword } from "../../../redux/slice/adminUserSlice";
import { useLoader } from "../../../context/LoaderContext";
import { useToast } from "../../../context/TostContext";
import ListSection from "./ListSection";
import PasswordChangeModal from "../../../components/modals/PasswordChangeModal";
import AdminModal from "./AdminModal";

import { usePermission } from "../../../hooks/usePermission";

const MODULE_SLUG = "admin_users";

const AdminUsers = () => {
  const dispatch = useDispatch();
  const { setLoading } = useLoader();
  const headerPortalTarget = document.getElementById("header-portal-target");

    const headerContent = (
    <div className="d-flex align-items-center justify-content-between w-100">
      <h4 className="fw-semibold mb-0 text-primary fs-lg">
        <span className="text-muted fw-normal">Admin Management / </span>Users
      </h4>
    </div>
  );
  const toast = useToast();

  const { users = [], isLoading } = useSelector((state) => state.adminUsers || {});

  const [openForm, setOpenForm] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isView, setIsView] = useState(false);

  const { hasPermission } = usePermission();
  const canCreate = hasPermission(MODULE_SLUG, "create");
  const canUpdate = hasPermission(MODULE_SLUG, "update");
  const canDelete = hasPermission(MODULE_SLUG, "delete");

  const fetchData = useCallback(() => {
    setLoading(true);
    dispatch(fetchAdminUsers()).finally(() => setLoading(false));
  }, [dispatch, setLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddUser = () => {
    setSelectedUser(null);
    setOpenForm(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsView(false);
    setOpenForm(true);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setIsView(true);
    setOpenForm(true);
  };

  const handleChangePassword = (user) => {
    setSelectedUser(user);
    setOpenPasswordModal(true);
  };

  const handleDeleteUser = (id) => {
    setLoading(true);
    dispatch(deleteAdminUser(id))
      .then((res) => {
        if (res.error) {
          toast.error(res.payload?.message || "Failed to update status");
        } else {
          toast.success("User status updated successfully");
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
      <Container fluid className="px-0">
      <div className="bg-white rounded-2 overflow-hidden common-shadow">
        <ListSection
          data={users}
          isLoading={isLoading}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onAdd={canCreate ? handleAddUser : null}
          onEdit={canUpdate ? handleEditUser : null}
          onView={handleViewUser}
          onDelete={canDelete ? handleDeleteUser : null}
          onChangePassword={canUpdate ? handleChangePassword : null}
        />
      </div>

      <AdminModal
        isOpen={openForm}
        onClose={() => {
          setOpenForm(false);
          setIsView(false);
        }}
        title={isView ? "View Admin User" : selectedUser ? "Update Admin User" : "Add Admin User"}
        size="lg"
        user={selectedUser}
        isView={isView}
        onSuccess={fetchData}
      />

      {openPasswordModal && (
        <PasswordChangeModal
          isOpen={openPasswordModal}
          onClose={() => setOpenPasswordModal(false)}
          onSubmit={async (newPassword) => {
            await dispatch(changeAdminPassword({ id: selectedUser?.id, new_password: newPassword })).unwrap();
          }}
          title={`Change Password for ${selectedUser?.name}`}
        />
      )}
    </Container>
  </>
  );
};

export default AdminUsers;
